import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { auth, GoogleAuthProvider, FacebookAuthProvider } from '../Firebase';
import { isFacebookLoginOn, facebookErrorMessage, linkHeldFacebook } from '../utils/facebookLogin';
import { onAuthStateChanged } from 'firebase/auth';
import CommunityPreview from './CommunityPreview';
import { isDisallowedImageUrl } from '../utils/imageUrlSafety';
import './CommunityBuilder.css';
import { useInAppGoogleGuard } from '../components/OpenInBrowserPrompt';
import useCommunityPageAnalytics from '../hooks/useCommunityPageAnalytics';

const API = process.env.REACT_APP_API_BASE_URL;

const EMPTY_FIELDS = {
  title: '', location: '', type: '', homePageTagline: '',
  topLevelSummary: '', mainDescription: '', eligibilityDescription: '',
  tags: '', imageQuery: '', heroTagline: '', socialDescription: '',
  connectionReasons: 'Connect,Meet up,Share experiences,Make friends'
};

const ANON_DRAFT_KEY = 'cb_anon_draft';
const ANON_DRAFT_TTL = 60 * 60 * 1000; // 1 hour
const CB_SESSION_KEY = 'cb_session_id';

function getOrCreateSessionId() {
  try {
    let id = localStorage.getItem(CB_SESSION_KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
      localStorage.setItem(CB_SESSION_KEY, id);
    }
    return id;
  } catch (_) { return null; }
}

function saveAnonDraft(data) {
  try {
    localStorage.setItem(ANON_DRAFT_KEY, JSON.stringify({ ...data, savedAt: Date.now() }));
  } catch (_) {}
}

function loadAnonDraft() {
  try {
    const raw = localStorage.getItem(ANON_DRAFT_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.savedAt > ANON_DRAFT_TTL) { localStorage.removeItem(ANON_DRAFT_KEY); return null; }
    return data;
  } catch (_) { return null; }
}

function clearAnonDraft() {
  try { localStorage.removeItem(ANON_DRAFT_KEY); } catch (_) {}
}


const CommunityBuilder = () => {
  const { guard, prompt: openInBrowserPrompt } = useInAppGoogleGuard();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [selectedImages, setSelectedImages] = useState([]);
  const [suggestedImages, setSuggestedImages] = useState([]);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [imagesLoading, setImagesLoading] = useState(false);
  const [mode, setMode] = useState('gather');
  const [generated, setGenerated] = useState(false);
  const [previewLinkShown, setPreviewLinkShown] = useState(false);
  const [previewEditMode, setPreviewEditMode] = useState(true);
  const [memberFields, setMemberFields] = useState([]);
  const [memberFieldsLoading, setMemberFieldsLoading] = useState(false);
  const [memberFieldsGenerated, setMemberFieldsGenerated] = useState(false);
  const [visitedTabs, setVisitedTabs] = useState(new Set(['chat']));
  const [openSection, setOpenSection] = useState('chat');
  const [leaderEnabled, setLeaderEnabled] = useState(false);
  const [leaderName, setLeaderName] = useState('');
  const [leaderBio, setLeaderBio] = useState('');
  const [leaderImageUrl, setLeaderImageUrl] = useState('');
  const [vetting, setVetting] = useState({ requireEmailVerification: false, requireVideoIntro: false, requireManualApproval: false, approvedEmailsOnly: false, approvedEmails: '', requireEmailDomain: false, emailDomain: '', customConditions: '' });
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitDone, setSubmitDone] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [submitterName, setSubmitterName] = useState('');
  const [googleAuthLoading, setGoogleAuthLoading] = useState(false);
  const [facebookAuthLoading, setFacebookAuthLoading] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [showSaveNudge, setShowSaveNudge] = useState(false);
  const [splashAccordion, setSplashAccordion] = useState(null);
  const [needsAutoSave, setNeedsAutoSave] = useState(false);
  const [manualUrlError, setManualUrlError] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const imageSearchRef = useRef(null);
  const dragIndex = useRef(null);
  const prevFirebaseUserRef = useRef(undefined);

  useEffect(() => {
    // Restore anonymous draft on mount (works whether or not user is already signed in)
    const draft = loadAnonDraft();
    if (draft?.generated) {
      if (draft.fields) setFields(f => ({ ...f, ...draft.fields }));
      if (draft.messages?.length) setMessages(draft.messages);
      if (draft.selectedImages?.length) setSelectedImages(draft.selectedImages);
      if (draft.leaderName) { setLeaderName(draft.leaderName); setLeaderEnabled(draft.leaderEnabled || false); }
      if (draft.leaderBio) setLeaderBio(draft.leaderBio);
      if (draft.leaderImageUrl) setLeaderImageUrl(draft.leaderImageUrl);
      if (draft.vetting) setVetting(v => ({ ...v, ...draft.vetting }));
      if (draft.memberFields?.length) { setMemberFields(draft.memberFields); setMemberFieldsGenerated(true); }
      if (draft.visitedTabs?.length) setVisitedTabs(new Set(draft.visitedTabs));
      setGenerated(true);
      setMode(draft.mode || 'refine');
      setShowSplash(false);
      return;
    }
    setMessages([{
      sender: 'bot',
      text: "Hi! Tell me about the community you'd like to create — what's the idea?\n\nI'll instantly build a first draft from your answer. Then use the tabs above to preview it and come back here to refine anything you'd like to change."
    }]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist to localStorage whenever state changes (unauthenticated users)
  // Also save to server so we have a permanent record regardless of sign-up
  useEffect(() => {
    if (firebaseUser || !generated) return;
    const t = setTimeout(() => {
      saveAnonDraft({
        fields, messages, selectedImages,
        leaderEnabled, leaderName, leaderBio, leaderImageUrl,
        vetting, memberFields, generated, mode,
        visitedTabs: [...visitedTabs],
      });
      const sessionId = getOrCreateSessionId();
      if (sessionId && fields.title) {
        fetch(`${API}/community-builder/draft-anon`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            fields,
            images: selectedImages,
            leader: (leaderName || leaderBio || leaderImageUrl)
              ? { name: leaderName, bio: leaderBio, imageUrl: leaderImageUrl } : null,
            vetting,
            memberFields,
          }),
        }).catch(() => {});
      }
    }, 800);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, messages, selectedImages, leaderEnabled, leaderName, leaderBio, leaderImageUrl, vetting, memberFields, generated, mode, visitedTabs, firebaseUser]);

  // Visits, time, scroll and clicks — admin Page Analytics → "Create page (/create)".
  useCommunityPageAnalytics('page-create', { isLoggedIn: !!firebaseUser });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      const prev = prevFirebaseUserRef.current;
      prevFirebaseUserRef.current = u;
      setFirebaseUser(u);
      if (u) {
        setShowSplash(false);
        if (u.displayName) setLeaderName(p => p || u.displayName);
        if (u.photoURL) setLeaderImageUrl(p => p || u.photoURL);
        if (u.displayName) setSubmitterName(p => p || u.displayName);
        if (u.email) setSubmitterEmail(p => p || u.email);
        // If the user just signed in mid-build, flag for auto-save
        if (prev === null) setNeedsAutoSave(true);
      }
    });
    return unsub;
  }, []);

  // Restore draft when navigated from Community Manager
  useEffect(() => {
    const resumeId = location?.state?.resumeDraftId;
    if (!resumeId) return;
    setShowSplash(false);
    const restore = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const res = await fetch(`${API}/community-builder/draft/${resumeId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const draft = await res.json();
        if (draft.fields) setFields(f => ({ ...f, ...draft.fields }));
        if (draft.aiFields) {
          // restore AI conversation context if saved
        }
        if (draft.leaderName) { setLeaderName(draft.leaderName); setLeaderEnabled(true); }
        if (draft.leaderBio) setLeaderBio(draft.leaderBio);
        if (draft.leaderImageUrl) setLeaderImageUrl(draft.leaderImageUrl);
        if (draft.vetting) setVetting(v => ({ ...v, ...draft.vetting }));
        if (draft.memberFields?.length) { setMemberFields(draft.memberFields); setMemberFieldsGenerated(true); setVisitedTabs(prev => new Set([...prev, 'members'])); }
        if (draft.images?.length) setSelectedImages(draft.images);
        setDraftId(resumeId);
        setGenerated(true);
        setMode('refine');
        clearAnonDraft();
        setOpenSection('preview');
        setMessages(prev => [...prev, { sender: 'bot', text: `Welcome back! Your draft "${draft.fields?.title || 'community'}" has been restored. Use the tabs to continue editing.` }]);
      } catch { /* silent */ }
    };
    // Wait briefly for auth to settle
    const t = setTimeout(restore, 800);
    return () => clearTimeout(t);
  }, [location?.state?.resumeDraftId]);

  const handleGoogleSignIn = async () => {
    setGoogleAuthLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await auth.signInWithPopup(provider);
      await linkHeldFacebook(result.user); // after "already have an account" on Facebook
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') console.error('Google sign-in error:', err);
    } finally {
      setGoogleAuthLoading(false);
    }
  };

  const handleFacebookSignIn = async () => {
    setFacebookAuthLoading(true);
    try {
      const provider = new FacebookAuthProvider();
      provider.addScope('email');
      await auth.signInWithPopup(provider);
    } catch (err) {
      console.error('Facebook sign-in error:', err);
      const message = facebookErrorMessage(err);
      if (message) window.alert(message);
    } finally {
      setFacebookAuthLoading(false);
    }
  };

  const saveDraft = useCallback(async () => {
    if (!firebaseUser || !fields.title) return;
    try {
      const token = await firebaseUser.getIdToken();
      const leader = (leaderName || leaderBio || leaderImageUrl)
        ? { name: leaderName, bio: leaderBio, imageUrl: leaderImageUrl } : null;
      const res = await fetch(`${API}/community-builder/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ fields, images: selectedImages, leader, vetting: { ...vetting, approvedEmails: vetting.approvedEmails.split(',').map(e => e.trim()).filter(Boolean) }, memberFields, draftId })
      });
      if (res.ok) {
        const data = await res.json();
        setDraftId(data.id);
        setDraftSaved(true);
        setTimeout(() => setDraftSaved(false), 3000);
      }
    } catch (e) { /* silent */ }
  }, [firebaseUser, fields, selectedImages, leaderName, leaderBio, leaderImageUrl, vetting, draftId]);

  // Auto-save draft when user signs in mid-build (after re-render so saveDraft has fresh firebaseUser)
  useEffect(() => {
    if (needsAutoSave && firebaseUser && generated) {
      setNeedsAutoSave(false);
      saveDraft();
    }
  }, [needsAutoSave, firebaseUser, generated, saveDraft]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    if (fields.imageQuery && generated) {
      fetchAndAutoSelect(fields.imageQuery);
    }
  }, [fields.imageQuery, generated]);

  useEffect(() => {
    if (openSection) setVisitedTabs(prev => new Set([...prev, openSection]));
  }, [openSection]);

  useEffect(() => {
    if (openSection === 'members' && generated && !memberFieldsGenerated && !memberFieldsLoading) {
      setMemberFieldsLoading(true);
      fetch(`${API}/community-builder/member-fields`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields })
      })
        .then(r => r.json())
        .then(data => { if (data.fields) { setMemberFields(data.fields); setMemberFieldsGenerated(true); } })
        .catch(() => {})
        .finally(() => setMemberFieldsLoading(false));
    }
  }, [openSection, generated, memberFieldsGenerated, memberFieldsLoading]);

  useEffect(() => {
    if (firebaseUser || showSplash) return;
    const id = setInterval(() => {
      if (!firebaseUser) setShowSaveNudge(true);
    }, 3 * 60 * 1000);
    return () => clearInterval(id);
  }, [firebaseUser, showSplash]);

  const [flashTab, setFlashTab] = useState(null);
  const [isFlashing, setIsFlashing] = useState(false);

  const toggle = (section) => {
    if (isFlashing) return;
    if (section === 'preview' && openSection === 'chat') {
      navigateToPreview();
    } else {
      setOpenSection(section);
    }
  };

  const navigateToPreview = () => {
    const intermediate = ['images', 'settings', 'members'];
    setIsFlashing(true);
    let delay = 0;
    intermediate.forEach(id => {
      delay += 250;
      setTimeout(() => setFlashTab(id), delay);
      setTimeout(() => setFlashTab(null), delay + 200);
    });
    setTimeout(() => {
      setIsFlashing(false);
      setOpenSection('preview');
    }, delay + 250);
  };

  const goToPreview = () => navigateToPreview();

  const navigateToChat = () => {
    const intermediate = ['members', 'settings', 'images'];
    setIsFlashing(true);
    let delay = 0;
    intermediate.forEach(id => {
      delay += 250;
      setTimeout(() => setFlashTab(id), delay);
      setTimeout(() => setFlashTab(null), delay + 200);
    });
    setTimeout(() => {
      setIsFlashing(false);
      setOpenSection('chat');
    }, delay + 250);
  };

  const buildApiMessages = (msgs) =>
    msgs.map(m => ({ role: m.sender === 'bot' ? 'assistant' : 'user', content: m.text }));

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isLoading) return;
    const userMsg = { sender: 'user', text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      const res = await fetch(`${API}/community-builder/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: buildApiMessages(newMessages), currentFields: fields, mode })
      });
      const data = await res.json();

      setMessages(prev => [...prev, { sender: 'bot', text: data.message || 'Something went wrong.' }]);

      if (data.fields) {
        if (data.reset) {
          // Complete redo — replace all fields and wipe images so fresh ones are fetched
          setFields({ ...EMPTY_FIELDS, ...data.fields });
          setSelectedImages([]);
          setSuggestedImages([]);
          setMemberFields([]);
          setMemberFieldsGenerated(false);
        } else {
          setFields(prev => {
            const merged = { ...prev };
            Object.entries(data.fields).forEach(([k, v]) => { if (v && String(v).trim()) merged[k] = v; });
            return merged;
          });
        }
      }

      if (data.memberFields && Array.isArray(data.memberFields)) {
        setMemberFields(data.memberFields);
        setMemberFieldsGenerated(true);
      }

      if (data.ready && mode === 'gather') {
        setGenerated(true);
        setMode('refine');
        setPreviewLinkShown(true);
        setTimeout(() => {
          setMessages(prev => [...prev, {
            sender: 'bot',
            text: "Your community page is ready!",
            previewLink: true
          }]);
        }, 400);

        // Auto-save draft — for auth users use the full /draft endpoint,
        // for anon users save immediately to server so we always have a record
        if (!firebaseUser && data.fields) {
          const freshFields = { ...fields };
          Object.entries(data.fields).forEach(([k, v]) => { if (v && String(v).trim()) freshFields[k] = v; });
          const sessionId = getOrCreateSessionId();
          if (sessionId) {
            fetch(`${API}/community-builder/draft-anon`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ sessionId, fields: freshFields, images: [], leader: null, vetting: {}, memberFields: [] }),
            }).catch(() => {});
          }
        }
        if (firebaseUser && data.fields) {
          const freshFields = { ...fields };
          Object.entries(data.fields).forEach(([k, v]) => { if (v && String(v).trim()) freshFields[k] = v; });
          try {
            const token = await firebaseUser.getIdToken();
            const saveRes = await fetch(`${API}/community-builder/draft`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ fields: freshFields, images: [], leader: null, vetting: {}, draftId: null })
            });
            const saveData = await saveRes.json();
            if (saveData.id) { setDraftId(saveData.id); setDraftSaved(true); setTimeout(() => setDraftSaved(false), 3000); }
          } catch { /* silent */ }
        }
      }
    } catch {
      setMessages(prev => [...prev, { sender: 'bot', text: 'Something went wrong. Try again.' }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const fetchAndAutoSelect = async (query) => {
    setImagesLoading(true);
    try {
      const res = await fetch(`${API}/community-builder/images/pexels?q=${encodeURIComponent(query)}`);
      const data = await res.json();
      const photos = data.photos || [];
      setSuggestedImages(photos);
      setSelectedImages(photos.slice(0, 5));
    } catch { setSuggestedImages([]); }
    finally { setImagesLoading(false); }
  };

  const fetchImages = async (query) => {
    if (!query) return;
    setImagesLoading(true);
    try {
      const res = await fetch(`${API}/community-builder/images/pexels?q=${encodeURIComponent(query)}`);
      const data = await res.json();
      setSuggestedImages(data.photos || []);
    } catch { setSuggestedImages([]); }
    finally { setImagesLoading(false); }
  };

  const toggleImageSelection = (photo) => {
    setSelectedImages(prev => {
      const exists = prev.find(p => p.id === photo.id);
      return exists ? prev.filter(p => p.id !== photo.id) : [...prev, photo];
    });
  };

  const addManualUrl = () => {
    const url = imageUrlInput.trim();
    if (!url) return;
    if (isDisallowedImageUrl(url)) {
      setManualUrlError('Images of or relating to children are not permitted.');
      return;
    }
    setManualUrlError('');
    setSelectedImages(prev => [...prev, { id: `manual-${Date.now()}`, url, thumb: url, credit: 'Direct link' }]);
    setImageUrlInput('');
  };

  const removeSelectedImage = (id) => setSelectedImages(prev => prev.filter(p => p.id !== id));

  const [dragOverIdx, setDragOverIdx] = useState(null);

  const onDragStart = (i) => { dragIndex.current = i; };
  const onDragOver = (e, i) => {
    e.preventDefault();
    setDragOverIdx(i);
  };
  const onDrop = (i) => {
    if (dragIndex.current === null || dragIndex.current === i) {
      setDragOverIdx(null);
      return;
    }
    setSelectedImages(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(dragIndex.current, 1);
      updated.splice(i, 0, moved);
      return updated;
    });
    dragIndex.current = null;
    setDragOverIdx(null);
  };
  const onDragEnd = () => { dragIndex.current = null; setDragOverIdx(null); };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const handleSubmit = async () => {
    if (!submitterName.trim()) { setSubmitError('Please enter your full name.'); return; }
    if (!submitterEmail.trim()) { setSubmitError('Please enter your email address.'); return; }
    setSubmitting(true);
    setSubmitError('');
    const leader = (leaderName || leaderBio || leaderImageUrl) ? { name: leaderName, bio: leaderBio, imageUrl: leaderImageUrl } : null;
    const vettingData = { ...vetting, approvedEmails: vetting.approvedEmails.split(',').map(e => e.trim()).filter(Boolean) };
    try {
      const res = await fetch(`${API}/community-builder/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields, images: selectedImages, submitterEmail: submitterEmail.trim(), submitterName: submitterName.trim(), leader, vetting: vettingData, memberFields, draftId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      setSubmitDone(true);
      clearAnonDraft();
      localStorage.setItem('cb_has_communities', 'true');
      window.dispatchEvent(new Event('storage'));
    } catch (err) {
      setSubmitError(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { id: 'chat',     label: 'AI Builder' },
    { id: 'images',   label: 'Images' },
    { id: 'settings', label: 'Settings' },
    { id: 'members',  label: 'Member Profile' },
    { id: 'preview',  label: 'Preview' },
    { id: 'submit',   label: 'Submit' },
  ];

  const stepInfo = {
    chat:     'Describe your community idea — the AI builds a full page instantly. Come back here anytime to refine.',
    images:   'Choose images for your community page. The first image becomes the hero.',
    settings: 'Set your leader profile and membership requirements.',
    members:  'Choose what information members fill in when they join — the AI suggests relevant questions.',
    preview:  'This is how your community page will look to visitors.',
    submit:   'Review and submit your community for approval.',
  };

  if (showSplash) {
    const anonDraft = loadAnonDraft();
    const draftMinsLeft = anonDraft
      ? Math.max(1, Math.ceil((ANON_DRAFT_TTL - (Date.now() - anonDraft.savedAt)) / 60000))
      : null;

    return (
      <div className="cb-splash">
        <div className="cb-splash-inner">
          <div className="cb-splash-image-grid">
            {[
              'https://images.pexels.com/photos/5135108/pexels-photo-5135108.jpeg',
              'https://images.pexels.com/photos/7693656/pexels-photo-7693656.jpeg',
              'https://images.pexels.com/photos/6224647/pexels-photo-6224647.jpeg',
              'https://images.pexels.com/photos/30201978/pexels-photo-30201978.jpeg',
              'https://images.pexels.com/photos/11840357/pexels-photo-11840357.jpeg',
              'https://images.pexels.com/photos/1186349/pexels-photo-1186349.jpeg',
              'https://images.pexels.com/photos/4612886/pexels-photo-4612886.jpeg',
              'https://images.pexels.com/photos/7644877/pexels-photo-7644877.jpeg',
              'https://images.pexels.com/photos/28958826/pexels-photo-28958826.jpeg',
            ].map((url, i) => (
              <div key={i} className="cb-splash-grid-cell" style={{ backgroundImage: `url(${url})` }} />
            ))}
          </div>
          <h1 className="cb-splash-title">
            Build Your Own Community Introducing App
            <span className="cb-splash-title-sub">(...an Interconnector)</span>
          </h1>
          <p className="cb-splash-subtitle">Build in under 3 min!</p>

          <button className="cb-splash-btn cb-splash-btn--try cb-splash-cta" onClick={() => setShowSplash(false)}>
            Build now
          </button>
          <p className="cb-splash-free">Free to build. Free to use. No costs.</p>

          {anonDraft?.generated && (
            <button
              className="cb-splash-return-draft"
              onClick={() => setShowSplash(false)}
            >
              ↩ Return to my draft · expires in {draftMinsLeft} min{draftMinsLeft !== 1 ? 's' : ''}
            </button>
          )}

          <div className="cb-splash-features">
            <span>🔁 Matching algorithms</span>
            <span>👥 Member introductions</span>
            <span>🛂 Custom join criteria</span>
            <span>💬 Community messaging</span>
            <span>📋 Leader tools &amp; controls</span>
          </div>

          <p className="cb-splash-explore-label">What does this give you?</p>

          <div className="cb-splash-accordion">
            {[
              {
                id: 'different',
                icon: '🤔',
                title: 'How is this different to other social platforms?',
                body: (
                  <ul className="cb-splash-bullets cb-splash-bullets--inset">
                    <li><span>👍</span><span>On Facebook, you create a group to share social media and posts.</span></li>
                    <li><span>🎵</span><span>On TikTok, you create an account for those to follow.</span></li>
                    <li><span>📅</span><span>On Meetup, you create a group to organise events.</span></li>
                    <li className="cb-splash-bullets__highlight"><span>🎉</span><span>On the Interconnector, you create a group to get your members matched.</span></li>
                  </ul>
                )
              },
              {
                id: 'members',
                icon: '👥',
                title: 'What do members actually get?',
                body: <p className="cb-splash-acc-body">Members of your Interconnector group get suggested other members they might want to connect with, based on compatibility. If both people agree, they are connected over Messenger. Group matching is possible too. No broadcasting, no hoping someone responds.</p>
              },
              {
                id: 'dating',
                icon: '💡',
                title: 'Is this like a dating app?',
                body: <p className="cb-splash-acc-body">Interconnectors are not dating apps. They are for finding people with compatible interests, shared stories and the basis for true connection. The matching logic borrows from that world, but applied to something more meaningful: no endless swiping, no shallow filters. Members set their own compatibility threshold, and suggestions are based on genuine information: interests, values, circumstances. Not just "how tall are you". Dating apps: yawn.</p>
              },
              {
                id: 'perks',
                icon: '🌟',
                title: 'Perks of being a community leader',
                body: (
                  <ul className="cb-splash-bullets cb-splash-bullets--inset">
                    <li><span>📈</span><span>Reach and grow a large community.</span></li>
                    <li><span>📣</span><span>Set the agenda — suggest topics of discussion, events and knowledge tailored to your community.</span></li>
                    <li><span>🎨</span><span>Brand the community as your own.</span></li>
                    <li><span>🤝</span><span>Have the reward of bringing people together!</span></li>
                    <li><span>🔍</span><span>Learn about your community — gain insights into who your members are and what connects them.</span></li>
                  </ul>
                )
              },
              {
                id: 'leader',
                icon: '🎯',
                title: 'What do I get as community leader?',
                body: <p className="cb-splash-acc-body">You decide who can join, how members are profiled, and how the group is branded. You can message the whole community, make personalised connection suggestions, and set the agenda. Introductions are handled automatically by the platform. There is no cost, and as we are just launching, we work closely with community leaders to shape how it evolves.</p>
              },
              {
                id: 'matching',
                icon: '🔁',
                title: 'How does the matching work?',
                body: <p className="cb-splash-acc-body">Choose how members are introduced to each other — by mutual interest, complementary skills, proximity, or a custom blend. You can switch algorithm types as your community grows and set how often introductions are made.</p>
              },
              {
                id: 'messaging',
                icon: '💬',
                title: 'Can I message my whole community?',
                body: <p className="cb-splash-acc-body">Yes. Send updates, set agendas, start discussions, and broadcast announcements to all your members at once. You can also message individuals directly or create group threads around specific topics.</p>
              },
              {
                id: 'suggestions',
                icon: '✨',
                title: 'Can I make my own connection suggestions?',
                body: <p className="cb-splash-acc-body">Yes. As community leader you can make tailored recommendations — suggesting specific members connect with each other based on your own knowledge of the community, outside of the automated algorithm.</p>
              },
              {
                id: 'profiles',
                icon: '📋',
                title: 'Can members have custom profiles?',
                body: <p className="cb-splash-acc-body">Yes. Choose what information members share when they join — short answers, bio paragraphs, dropdown choices, checkboxes and more. The AI suggests relevant questions based on your community type.</p>
              },
              {
                id: 'criteria',
                icon: '🛂',
                title: 'Can I control who joins?',
                body: <p className="cb-splash-acc-body">Yes. Require verified email addresses, specific email domains (e.g. @cam.ac.uk), video introductions, manual approval, or an approved email list. Mix and match criteria to keep your community exactly who it should be.</p>
              },
              {
                id: 'page',
                icon: '🏠',
                title: 'Do I get my own community page?',
                body: <p className="cb-splash-acc-body">Yes — a dedicated public page with images, description, who it is for, your leader profile, and a join button. Ready to share and go live within minutes.</p>
              },
            ].map(({ id, icon, title, body }) => (
              <div key={id} className={`cb-splash-acc-item${splashAccordion === id ? ' open' : ''}`}>
                <button
                  className="cb-splash-acc-trigger"
                  onClick={() => setSplashAccordion(prev => prev === id ? null : id)}
                >
                  <span className="cb-splash-acc-icon">{icon}</span>
                  <span className="cb-splash-acc-title">{title}</span>
                  <span className="cb-splash-acc-chevron">{splashAccordion === id ? '▲' : '▼'}</span>
                </button>
                {splashAccordion === id && body}
              </div>
            ))}
          </div>
        </div>

      </div>
    );
  }

  return (
    <div className="cb-root cb-root--accordion">
      {openInBrowserPrompt}

      {/* 3-minute save nudge popup */}
      {showSaveNudge && !firebaseUser && (
        <div className="cb-popup-overlay" onClick={() => setShowSaveNudge(false)}>
          <div className="cb-popup" onClick={e => e.stopPropagation()}>
            <button className="cb-popup-close cb-remove-btn" onClick={() => setShowSaveNudge(false)}>✕</button>
            <p className="cb-popup-eyebrow">Don't lose your work</p>
            <h2 className="cb-popup-title">Save your community build</h2>
            <p className="cb-popup-desc">Sign in and your progress saves automatically — pick up where you left off any time, and go live when you're ready.</p>

            <div className="cb-popup-option cb-popup-option--primary">
              <button
                className="cb-splash-btn cb-splash-btn--google"
                onClick={guard(() => { setShowSaveNudge(false); handleGoogleSignIn(); })}
                disabled={googleAuthLoading}
                style={{ color: '#111827', width: '100%' }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908C16.618 14.013 17.64 11.706 17.64 9.2z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
                </svg>
                {googleAuthLoading ? 'Signing in…' : 'Continue with Google'}
              </button>
              {/* Hidden until the Meta app is Live — see utils/facebookLogin.js. */}
              {isFacebookLoginOn() && (
              <button
                className="cb-splash-btn cb-splash-btn--facebook"
                onClick={() => { setShowSaveNudge(false); handleFacebookSignIn(); }}
                disabled={facebookAuthLoading}
                style={{ width: '100%' }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                  <path fill="#fff" d="M18 9a9 9 0 1 0-10.406 8.89v-6.29H5.309V9h2.285V7.017c0-2.256 1.344-3.503 3.4-3.503.985 0 2.015.176 2.015.176v2.215h-1.135c-1.118 0-1.467.694-1.467 1.406V9h2.496l-.4 2.6H10.41v6.29A9.002 9.002 0 0 0 18 9z"/>
                </svg>
                {facebookAuthLoading ? 'Signing in…' : 'Continue with Facebook'}
              </button>
              )}
              <Link to="/create-account?back=/create" className="cb-popup-email-link">Create a free account with email →</Link>
              <Link to="/login?back=/create" className="cb-popup-signin-link">Already have an account? Sign in →</Link>
            </div>

            <button
              className="cb-popup-dismiss"
              onClick={() => setShowSaveNudge(false)}
            >Continue without saving</button>
          </div>
        </div>
      )}

      {/* STICKY TAB BAR */}
      <div className="cb-tab-bar">
        {tabs.map(t => {
          const isActive = openSection === t.id;
          const isFlashActive = flashTab === t.id;
          const isVisited = visitedTabs.has(t.id) && !isActive && t.id !== 'submit';
          return (
            <button
              key={t.id}
              className={`cb-tab ${(isActive || isFlashActive) ? 'cb-tab--active' : ''} ${isVisited ? 'cb-tab--completed' : ''}`}
              onClick={() => toggle(t.id)}
            >
              {isVisited && <span className="cb-tab-tick">✓</span>}
              <span className="cb-tab-label">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* STEP INFO STRIP */}
      {openSection && stepInfo[openSection] && (
        <div className="cb-step-strip">
          <span className="cb-step-desc">{stepInfo[openSection]}</span>
        </div>
      )}

      {/* CONTENT AREA */}
      <div className="cb-tab-content">

        {openSection === 'chat' && (
          <div className="cb-chat-pane cb-chat-pane--accordion">
            <div className="cb-messages">
              {messages.map((m, i) => (
                <div key={i} className={`cb-message cb-message--${m.sender}`}>
                  {m.sender === 'bot' && <div className="cb-avatar">AI</div>}
                  <div className="cb-bubble-wrap">
                    <div className="cb-bubble">{m.text}</div>
                    {generated && m.sender === 'bot' && i > 0 && i === messages.length - 1 && (
                      <button className="cb-preview-link" onClick={goToPreview}>
                        👁 Preview your community →
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="cb-message cb-message--bot">
                  <div className="cb-avatar">AI</div>
                  <div className="cb-bubble cb-typing"><span /><span /><span /></div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="cb-input-row">
              <div className="cb-input-wrapper">
                <textarea
                  ref={inputRef}
                  className="cb-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your answer..."
                  rows={1}
                />
              </div>
              <button className="cb-send-btn" onClick={sendMessage} disabled={isLoading || !input.trim()} />
            </div>
          </div>
        )}

        {openSection === 'images' && (
          <div className="cb-image-inner">
            <div className="cb-url-row">
              <input
                className="cb-url-input"
                type="url"
                placeholder="Paste image URL directly..."
                value={imageUrlInput}
                onChange={e => { setImageUrlInput(e.target.value); setManualUrlError(''); }}
                onKeyDown={e => e.key === 'Enter' && addManualUrl()}
              />
              <button className="cb-url-add-btn" onClick={addManualUrl}>Add</button>
            </div>
            {manualUrlError && <p style={{ color: '#ef4444', fontSize: '0.78rem', margin: '0' }}>{manualUrlError}</p>}
            <div className="cb-url-row">
              <input
                ref={imageSearchRef}
                className="cb-url-input"
                placeholder="Search Pexels for images..."
                defaultValue={fields.imageQuery}
                onKeyDown={e => { if (e.key === 'Enter') fetchImages(e.target.value); }}
              />
              <button className="cb-url-add-btn" onClick={() => fetchImages(imageSearchRef.current?.value)}>Search</button>
            </div>
            {selectedImages.length > 0 && (
              <div className="cb-selected-images">
                <p className="cb-selected-label">Selected ({selectedImages.length}) — first is hero · drag to reorder</p>
                <div className="cb-selected-thumbs">
                  {selectedImages.map((p, idx) => (
                    <div
                      key={p.id}
                      className={`cb-selected-thumb cb-selected-thumb--draggable${dragOverIdx === idx ? ' cb-selected-thumb--drop-target' : ''}`}
                      draggable
                      onDragStart={() => onDragStart(idx)}
                      onDragOver={e => onDragOver(e, idx)}
                      onDrop={() => onDrop(idx)}
                      onDragEnd={onDragEnd}
                    >
                      <img src={p.thumb} alt="" />
                      {idx === 0 && <div className="cb-thumb-hero-badge">Hero</div>}
                      <button className="cb-remove-img" onClick={() => removeSelectedImage(p.id)}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {imagesLoading ? (
              <div className="cb-images-loading">Searching Pexels...</div>
            ) : suggestedImages.length > 0 ? (
              <div className="cb-image-grid">
                {suggestedImages.map(photo => {
                  const isSelected = selectedImages.some(p => p.id === photo.id);
                  return (
                    <div
                      key={photo.id}
                      className={`cb-image-thumb ${isSelected ? 'cb-image-thumb--selected' : ''}`}
                      onClick={() => toggleImageSelection(photo)}
                    >
                      <img src={photo.thumb} alt={photo.credit} />
                      {isSelected && <div className="cb-image-check">✓</div>}
                      <div className="cb-image-credit">{photo.credit}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="cb-images-empty">Images will auto-load once your community is generated.</p>
            )}

          </div>
        )}

        {openSection === 'members' && (
          <div className="cb-submit-form">
            <div className="cb-settings-intro">
              <strong>Member profile questions</strong>
              When someone joins your community, they'll fill in a short profile so you and other members can learn about them. Edit, remove or add questions below.
            </div>

            {memberFieldsLoading && (
              <div className="cb-generating">
                <div className="cb-generating-spinner" />
                <p className="cb-generating-label">Building your member profile questions…</p>
                <p className="cb-generating-sub">The AI is tailoring questions to your community</p>
              </div>
            )}

            {memberFields.length > 0 && (
              <div>
                <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem 1.25rem 1.5rem', marginBottom: '1rem', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', paddingBottom: '1rem', borderBottom: '2px solid #f1f5f9' }}>
                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>👤</div>
                    <div>
                      <p style={{ margin: 0, fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>Member Profile</p>
                      <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>{fields.title || 'Your community'}</p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    {memberFields.map((f, i) => (
                      <div key={i} style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '1.1rem' }}>
                        {/* Question label + type + remove */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
                          <input
                            value={f.label}
                            placeholder="Type your question here..."
                            onChange={e => setMemberFields(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                            style={{ flex: 1, fontSize: '0.88rem', fontWeight: 700, color: '#0f172a', background: 'transparent', border: 'none', borderBottom: '1.5px dashed #cbd5e1', outline: 'none', padding: '0.1rem 0', fontFamily: 'inherit' }}
                          />
                          <select
                            value={f.type}
                            onChange={e => setMemberFields(prev => prev.map((x, j) => j === i ? { ...x, type: e.target.value, options: (e.target.value === 'select' || e.target.value === 'multicheck') && !x.options?.length ? [''] : x.options } : x))}
                            style={{ fontSize: '0.72rem', padding: '0.2rem 0.35rem', borderRadius: 5, border: '1px solid #d1d5db', background: '#f8fafc', color: '#374151', cursor: 'pointer', flexShrink: 0 }}
                          >
                            <option value="text">Short text</option>
                            <option value="textarea">Paragraph</option>
                            <option value="select">Dropdown</option>
                            <option value="boolean">Yes / No</option>
                            <option value="multicheck">Checkboxes</option>
                            <option value="number">Number</option>
                          </select>
                          <button
                            onClick={() => setMemberFields(prev => prev.filter((_, j) => j !== i))}
                            title="Remove question"
                            className="cb-remove-btn"
                          >✕</button>
                        </div>

                        {/* Options editor — clean, no yellow box */}
                        {(f.type === 'select' || f.type === 'multicheck') && (
                          <div style={{ marginBottom: '0.5rem', paddingLeft: '0.25rem' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.35rem' }}>
                              {(f.options || []).map((opt, oi) => (
                                <div key={oi} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  {f.type === 'multicheck' && (
                                    <div style={{ flexShrink: 0, width: 14, height: 14, borderRadius: 3, border: '1.5px solid #9ca3af', background: '#fff' }} />
                                  )}
                                  {f.type === 'select' && (
                                    <div style={{ flexShrink: 0, width: 6, height: 6, borderRadius: '50%', background: '#9ca3af' }} />
                                  )}
                                  <input
                                    value={opt}
                                    onChange={e => setMemberFields(prev => prev.map((x, j) => j === i ? { ...x, options: x.options.map((o, k) => k === oi ? e.target.value : o) } : x))}
                                    style={{ flex: 1, border: 'none', borderBottom: '1px solid #e5e7eb', padding: '0.15rem 0', fontSize: '0.83rem', color: '#1e293b', background: 'transparent', fontFamily: 'inherit', outline: 'none' }}
                                  />
                                  <button
                                    onClick={() => setMemberFields(prev => prev.map((x, j) => j === i ? { ...x, options: x.options.filter((_, k) => k !== oi) } : x))}
                                    className="cb-remove-btn cb-remove-btn--sm"
                                  >✕</button>
                                </div>
                              ))}
                            </div>
                            <button
                              onClick={() => setMemberFields(prev => prev.map((x, j) => j === i ? { ...x, options: [...(x.options || []), ''] } : x))}
                              className="cb-add-option-btn"
                            >+ Add option</button>
                          </div>
                        )}

                        {/* Preview input */}
                        {f.type === 'textarea' && <textarea disabled rows={2} placeholder="Member writes here…" style={{ width: '100%', borderRadius: 7, border: '1.5px solid #e2e8f0', padding: '0.45rem 0.65rem', fontSize: '0.85rem', color: '#64748b', background: '#f8fafc', resize: 'none', boxSizing: 'border-box' }} />}
                        {f.type === 'text' && <input disabled placeholder="Short answer…" style={{ width: '100%', borderRadius: 7, border: '1.5px solid #e2e8f0', padding: '0.45rem 0.65rem', fontSize: '0.85rem', color: '#64748b', background: '#f8fafc', boxSizing: 'border-box' }} />}
                        {f.type === 'number' && <input disabled type="number" placeholder="e.g. 5" style={{ width: '100%', borderRadius: 7, border: '1.5px solid #e2e8f0', padding: '0.45rem 0.65rem', fontSize: '0.85rem', color: '#64748b', background: '#f8fafc', boxSizing: 'border-box' }} />}
                        {f.type === 'select' && (
                          <select disabled style={{ width: '100%', borderRadius: 7, border: '1.5px solid #e2e8f0', padding: '0.45rem 0.65rem', fontSize: '0.85rem', color: '#64748b', background: '#f8fafc', boxSizing: 'border-box' }}>
                            <option>Select an option…</option>
                            {(f.options || []).filter(Boolean).map((o, oi) => <option key={oi}>{o}</option>)}
                          </select>
                        )}
                        {f.type === 'boolean' && (
                          <div style={{ display: 'flex', gap: '1.5rem' }}>
                            {['Yes', 'No'].map(opt => (
                              <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.88rem', color: '#374151', fontWeight: 500 }}>
                                <input type="radio" disabled name={`bool-${i}`} style={{ accentColor: '#6366f1' }} /> {opt}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => setMemberFields(prev => [...prev, { label: '', type: 'text', icon: '✏️' }])}
                  style={{ width: '100%', background: 'transparent', border: '2px dashed #cbd5e1', borderRadius: 10, padding: '0.65rem', color: '#475569', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                >+ Add a question</button>
              </div>
            )}
          </div>
        )}

        {openSection === 'preview' && (
          <div className="cb-preview-scroll" style={{ position: 'relative' }}>
            <div className="cb-preview-mode-bar">
              <button
                className="cb-preview-mode-btn cb-preview-ai-btn"
                onClick={navigateToChat}
              >💬 Ask AI to make changes</button>
              <div style={{ flex: 1 }} />
              <button
                className={`cb-preview-mode-btn${previewEditMode ? ' active' : ''}`}
                onClick={() => setPreviewEditMode(true)}
              >✏️ Edit mode</button>
              <button
                className={`cb-preview-mode-btn${!previewEditMode ? ' active' : ''}`}
                onClick={() => setPreviewEditMode(false)}
              >👁 Final view</button>
            </div>
            <CommunityPreview
              fields={fields}
              images={selectedImages}
              leader={leaderName || leaderBio || leaderImageUrl ? { name: leaderName, bio: leaderBio, imageUrl: leaderImageUrl } : null}
              vetting={vetting}
              onFieldChange={previewEditMode ? (key, val) => setFields(prev => ({ ...prev, [key]: val })) : null}
              onLeaderChange={previewEditMode ? (l, goToSettings) => { setLeaderName(l.name); setLeaderBio(l.bio); setLeaderImageUrl(l.imageUrl); if (goToSettings) setOpenSection('settings'); } : null}
            />
          </div>
        )}

        {openSection === 'settings' && (
          <div className="cb-submit-form">
            <div className="cb-settings-intro">
              <strong>You're in control of your community.</strong>
              As community leader, you set the tone, define who can join, and decide how members interact. You'll be responsible for welcoming new members, maintaining the community's standards, and shaping how it grows — whether that's a tight-knit local group or a wide open network. The criteria you choose here will be shown to prospective members before they apply.
            </div>

            {/* Community Leader */}
            <div className="cb-leader-section">
              <div className="cb-leader-header">
                <span className="cb-leader-icon">👤</span>
                <div>
                  <p className="cb-leader-title">Community Leader <span className="cb-leader-optional">(optional)</span></p>
                  <p className="cb-leader-hint">Add yourself as the face of this community — shows on the preview page.</p>
                </div>
              </div>
              <label className="cb-vetting-opt" style={{ marginBottom: '1rem' }}>
                <input
                  type="checkbox"
                  checked={leaderEnabled}
                  onChange={e => {
                    setLeaderEnabled(e.target.checked);
                    if (e.target.checked) {
                      if (firebaseUser) {
                        setLeaderName(prev => prev || firebaseUser.displayName || '');
                        setLeaderImageUrl(prev => prev || firebaseUser.photoURL || '');
                      }
                    } else {
                      setLeaderName(''); setLeaderBio(''); setLeaderImageUrl('');
                    }
                  }}
                />
                <div>
                  <span className="cb-vetting-opt-label">Add myself as community leader</span>
                  <span className="cb-vetting-opt-desc">Your name, photo and bio will appear on the community page</span>
                </div>
              </label>
              {leaderEnabled && <div className="cb-leader-fields">
                <label
                  className="cb-leader-photo-upload"
                  style={leaderImageUrl ? { backgroundImage: `url(${leaderImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
                >
                  {!leaderImageUrl && <span>+ Photo</span>}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={e => {
                      const file = e.target.files[0];
                      if (file) setLeaderImageUrl(URL.createObjectURL(file));
                    }}
                  />
                </label>
                <div className="cb-leader-text-fields">
                  <input
                    className="cb-submit-input"
                    placeholder="Your name"
                    value={leaderName}
                    onChange={e => setLeaderName(e.target.value)}
                  />
                  <textarea
                    className="cb-submit-input cb-leader-bio"
                    placeholder="A short intro about you as the community leader..."
                    rows={3}
                    value={leaderBio}
                    onChange={e => setLeaderBio(e.target.value)}
                  />
                </div>
              </div>}
              {(leaderName || leaderBio) && (
                <p className="cb-leader-account-note">
                  You'll need a free Interconnector account to manage your community. <Link to="/login" target="_blank" rel="noreferrer">Create one here →</Link>
                </p>
              )}
            </div>

            {/* Membership vetting */}
            <div className="cb-vetting-section">
              <p className="cb-vetting-title">Membership vetting <span className="cb-leader-optional">(optional)</span></p>
              <p className="cb-vetting-hint">Choose what's required before someone can join your community.</p>
              <div className="cb-vetting-options">
                <label className="cb-vetting-opt cb-vetting-opt--open">
                  <input
                    type="radio"
                    name="vettingMode"
                    checked={!vetting.requireEmailVerification && !vetting.requireEmailDomain && !vetting.requireVideoIntro && !vetting.requireManualApproval && !vetting.approvedEmailsOnly}
                    onChange={() => setVetting(prev => ({ ...prev, requireEmailVerification: false, requireEmailDomain: false, requireVideoIntro: false, requireManualApproval: false, approvedEmailsOnly: false }))}
                  />
                  <div>
                    <span className="cb-vetting-opt-label">🌐 Open to all</span>
                    <span className="cb-vetting-opt-desc">Anyone can join — no requirements</span>
                  </div>
                </label>
                {[
                  { key: 'requireEmailVerification', label: '✉️ Verified email address', desc: 'Members must have a verified email to join' },
                  { key: 'requireEmailDomain', label: '🏫 Specific email domain', desc: 'Members must sign up with a particular email handle, e.g. @cam.ac.uk' },
                  { key: 'requireVideoIntro', label: '🎥 Video introduction', desc: 'Members submit a short video intro before joining' },
                  { key: 'requireManualApproval', label: '👤 Manual approval', desc: 'You personally approve each join request' },
                  { key: 'approvedEmailsOnly', label: '📋 Approved list only', desc: 'Only people on your approved email list can join' },
                ].map(opt => (
                  <label key={opt.key} className="cb-vetting-opt">
                    <input
                      type="checkbox"
                      checked={vetting[opt.key]}
                      onChange={e => setVetting(prev => ({ ...prev, [opt.key]: e.target.checked }))}
                    />
                    <div>
                      <span className="cb-vetting-opt-label">{opt.label}</span>
                      <span className="cb-vetting-opt-desc">{opt.desc}</span>
                    </div>
                  </label>
                ))}
              </div>
              {vetting.requireEmailDomain && (
                <div style={{ marginTop: '0.75rem' }}>
                  <label className="cb-submit-label">Required email domain(s)</label>
                  <input
                    className="cb-submit-input"
                    placeholder="e.g. @cam.ac.uk or @cam.ac.uk, @ox.ac.uk"
                    value={vetting.emailDomain}
                    onChange={e => setVetting(prev => ({ ...prev, emailDomain: e.target.value }))}
                  />
                  <p style={{ margin: '0.3rem 0 0', fontSize: '0.75rem', color: '#64748b' }}>Separate multiple domains with a comma. Members will only be able to join if their email ends with one of these.</p>
                </div>
              )}
              {vetting.approvedEmailsOnly && (
                <div style={{ marginTop: '0.75rem' }}>
                  <label className="cb-submit-label">Approved email addresses (comma-separated)</label>
                  <textarea
                    className="cb-submit-input cb-submit-textarea"
                    placeholder="alice@example.com, bob@example.com..."
                    rows={3}
                    value={vetting.approvedEmails}
                    onChange={e => setVetting(prev => ({ ...prev, approvedEmails: e.target.value }))}
                  />
                </div>
              )}

              <div style={{ marginTop: '1.25rem' }}>
                <label className="cb-submit-label">Any other conditions to join? <span className="cb-leader-optional">(optional)</span></label>
                <p className="cb-vetting-hint" style={{ marginBottom: '0.5rem' }}>E.g. must live in Berlin, must be a university student, must already be part of X community...</p>
                <textarea
                  className="cb-submit-input cb-submit-textarea"
                  placeholder="Must live in Berlin&#10;Must be enrolled at a university&#10;Must be a member of the Berlin Cycling Club..."
                  rows={3}
                  value={vetting.customConditions}
                  onChange={e => setVetting(prev => ({ ...prev, customConditions: e.target.value }))}
                />
              </div>
            </div>

            {firebaseUser ? (
              <button className="cb-draft-btn" onClick={saveDraft}>
                {draftSaved ? '✓ Draft saved' : '💾 Save draft'}
              </button>
            ) : (
              <p className="cb-vetting-hint" style={{ marginTop: '0.5rem' }}>
                <Link to="/login" style={{ color: '#f59e0b' }}>Log in or sign up</Link> to save your draft and manage your communities.
              </p>
            )}
          </div>
        )}

        {openSection === 'submit' && (() => {
          const reviewableTabs = tabs.filter(t => t.id !== 'submit');
          const unvisited = reviewableTabs.filter(t => !visitedTabs.has(t.id));
          return (
            <div className="cb-submit-form">
              {submitDone ? (
                <div className="cb-submit-success">
                  <p>Your community has been submitted for review. We'll be in touch soon!</p>
                </div>
              ) : (
                <>
                  {/* Pre-submit checklist — only show if any tab unvisited */}
                  {unvisited.length > 0 && (
                    <div className="cb-submit-checklist">
                      <p className="cb-submit-checklist-title">Before you submit</p>
                      <div className="cb-submit-checklist-rows">
                        {reviewableTabs.map(t => {
                          const done = visitedTabs.has(t.id);
                          return (
                            <div key={t.id} className={`cb-submit-check-row ${done ? 'cb-submit-check-row--done' : 'cb-submit-check-row--todo'}`}>
                              <span className="cb-submit-check-icon">{done ? '✓' : '○'}</span>
                              <span className="cb-submit-check-label">{t.label}</span>
                              {!done && (
                                <button className="cb-submit-check-go" onClick={() => toggle(t.id)}>
                                  Review →
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <p className="cb-submit-checklist-hint">These tabs are worth a look before submitting.</p>
                    </div>
                  )}

                  {/* Submit form — login gate if not signed in */}
                  {!firebaseUser ? (
                    <div className="cb-auth-prompt">
                      <div className="cb-auth-prompt-icon">🚀</div>
                      <h3 className="cb-auth-prompt-title">Sign in to submit</h3>
                      <p className="cb-auth-prompt-desc">
                        You need a free account to submit and manage your community. Once live you can track applications, message members, and manage settings.
                      </p>
                      <button className="cb-google-auth-btn" onClick={guard(handleGoogleSignIn)} disabled={googleAuthLoading}>
                        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                          <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908C16.618 14.013 17.64 11.706 17.64 9.2z"/>
                          <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                          <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                          <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
                        </svg>
                        {googleAuthLoading ? 'Signing in…' : 'Continue with Google'}
                      </button>
                      {/* Hidden until the Meta app is Live — see utils/facebookLogin.js. */}
                      {isFacebookLoginOn() && (
                      <button className="cb-facebook-auth-btn" onClick={handleFacebookSignIn} disabled={facebookAuthLoading}>
                        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                          <path fill="#fff" d="M18 9a9 9 0 1 0-10.406 8.89v-6.29H5.309V9h2.285V7.017c0-2.256 1.344-3.503 3.4-3.503.985 0 2.015.176 2.015.176v2.215h-1.135c-1.118 0-1.467.694-1.467 1.406V9h2.496l-.4 2.6H10.41v6.29A9.002 9.002 0 0 0 18 9z"/>
                        </svg>
                        {facebookAuthLoading ? 'Signing in…' : 'Continue with Facebook'}
                      </button>
                      )}
                      <div className="cb-auth-divider"><span>or</span></div>
                      <Link to="/create-account?back=/create" className="cb-auth-email-link">Create account with email →</Link>
                      <Link to="/login?back=/create" className="cb-auth-email-link" style={{ marginTop: '0.25rem' }}>Already have an account? Sign in →</Link>
                    </div>
                  ) : (
                    <>
                      <p className="cb-submit-intro">Nearly there! We just need a couple of details before we review and publish your community.</p>
                      <label className="cb-submit-label">Your full name <span style={{ color: '#ef4444' }}>*</span></label>
                      <input className="cb-submit-input" type="text" placeholder="Your full name" value={submitterName} onChange={e => setSubmitterName(e.target.value)} />
                      <label className="cb-submit-label">Your email address <span style={{ color: '#ef4444' }}>*</span></label>
                      <input className="cb-submit-input" type="email" placeholder="your@email.com" value={submitterEmail} onChange={e => setSubmitterEmail(e.target.value)} />
                      <label className="cb-submit-label">Community name</label>
                      <input className="cb-submit-input" type="text" value={fields.title} onChange={e => setFields(prev => ({ ...prev, title: e.target.value }))} />
                      <label className="cb-submit-label">Location</label>
                      <input className="cb-submit-input" type="text" value={fields.location} onChange={e => setFields(prev => ({ ...prev, location: e.target.value }))} />
                      <label className="cb-submit-label">Anything else you'd like us to know?</label>
                      <textarea className="cb-submit-input cb-submit-textarea" placeholder="Optional notes..." rows={3} />
                      {submitError && <p className="cb-submit-error">{submitError}</p>}
                      <button className="cb-submit-btn" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? 'Submitting...' : 'Submit Community for Review'}
                      </button>
                      <p className="cb-submit-note">We review all communities before publishing — usually within 24–48 hours.</p>
                      <p className="cb-submit-note"><Link to="/community-manager" style={{ color: '#6366f1' }}>View all your communities →</Link></p>
                    </>
                  )}
                </>
              )}
            </div>
          );
        })()}

      </div>

    </div>
  );
};

export default CommunityBuilder;
